"use client";

import { Input as InputPrimitive } from "@base-ui/react/input";
import type * as React from "react";

import { cn } from "~/lib/utils";
import { NO_ZOOM_FIELD_TEXT_CLASS } from "~/lib/touchTarget";

type InputProps = Omit<InputPrimitive.Props & React.RefAttributes<HTMLInputElement>, "size"> & {
  size?: "sm" | "default" | "lg" | number;
  unstyled?: boolean;
  nativeInput?: boolean;
};

function Input({
  className,
  size = "default",
  unstyled = false,
  nativeInput = false,
  ...props
}: InputProps) {
  const inputClassName = cn(
    // Mobile-first: a text field is a touch target below `md`, so it takes the
    // 48px minimum and a matching line-height (an <input> centres its text by
    // line-height, not by box alignment). From `md` up it drops to the dense
    // pointer scale. See DESIGN.md -> "Mobile-First Responsiveness".
    "min-h-touch w-full min-w-0 rounded-[inherit] px-[calc(--spacing(3)-1px)] leading-(--spacing-touch) outline-none placeholder:text-muted-foreground/72 md:h-7.5 md:min-h-0 md:leading-7.5 [transition:background-color_5000000s_ease-in-out_0s]",
    size === "sm" && "px-[calc(--spacing(2.5)-1px)] md:h-6.5 md:leading-6.5",
    size === "lg" && "md:h-8.5 md:leading-8.5",
    props.type === "search" &&
      "[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none [&::-webkit-search-results-button]:appearance-none [&::-webkit-search-results-decoration]:appearance-none",
    props.type === "file" &&
      "text-muted-foreground file:me-3 file:bg-transparent file:font-medium file:text-foreground file:text-sm",
  );

  return (
    <span
      className={
        cn(
          !unstyled &&
            `relative inline-flex w-full rounded-lg border border-input bg-background not-dark:bg-clip-padding ${NO_ZOOM_FIELD_TEXT_CLASS} text-foreground shadow-xs/5 ring-ring/24 transition-shadow before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-lg)-1px)] not-has-disabled:not-has-focus-visible:not-has-aria-invalid:before:shadow-[0_1px_--theme(--color-black/4%)] has-focus-visible:has-aria-invalid:border-destructive/64 has-focus-visible:has-aria-invalid:ring-destructive/16 has-aria-invalid:border-destructive-border has-focus-visible:border-ring has-autofill:bg-foreground/4 has-disabled:opacity-64 has-[:disabled,:focus-visible,[aria-invalid]]:shadow-none has-focus-visible:ring-[3px] dark:bg-input/32 dark:has-autofill:bg-foreground/8 dark:has-aria-invalid:ring-destructive-border dark:not-has-disabled:not-has-focus-visible:not-has-aria-invalid:before:shadow-[0_-1px_--theme(--color-white/6%)]`,
          className,
        ) || undefined
      }
      data-size={size}
      data-slot="input-control"
    >
      {nativeInput ? (
        <input
          className={inputClassName}
          data-slot="input"
          size={typeof size === "number" ? size : undefined}
          {...props}
          // @base-ui/react 1.6 widened Input.Props["style"] to also accept a
          // (state) => CSSProperties function for state-driven styling. That is
          // valid for the primitive below, but a native <input> only takes a
          // plain object, so the function form is dropped on this branch. No
          // caller can be relying on it: the prop type did not allow a function
          // before this bump.
          style={typeof props.style === "function" ? undefined : props.style}
        />
      ) : (
        <InputPrimitive
          className={inputClassName}
          data-slot="input"
          size={typeof size === "number" ? size : undefined}
          {...props}
        />
      )}
    </span>
  );
}

export { Input, type InputProps };
